'use client';

import { createContext, useContext, type ReactNode } from 'react';

const ServerAuthContext = createContext<boolean>(false);

interface ServerAuthProviderProps {
  /** True when the request arrived with a `shot_auth_token` cookie.
   * Server-side detection only — does NOT mutate as the client logs
   * in or out at runtime; for that, use `useIsAuthed()` from
   * stores/authStore. This value exists solely to seed components
   * (e.g. RequiresLogin) so the SSR HTML matches what an authed user
   * will see on first paint, eliminating the
   * "skeleton → content / login wall" flash on every refresh. */
  hasAuthCookie: boolean;
  children: ReactNode;
}

export function ServerAuthProvider({ hasAuthCookie, children }: ServerAuthProviderProps) {
  return (
    <ServerAuthContext.Provider value={hasAuthCookie}>{children}</ServerAuthContext.Provider>
  );
}

/**
 * Cookie presence at the moment the route was rendered on the server.
 * Always reflects the SSR snapshot — never updates client-side. Use
 * for first-paint UI decisions; for live auth state use the auth
 * store (`useIsAuthed`).
 */
export function useServerHasAuth(): boolean {
  return useContext(ServerAuthContext);
}
